import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiFolder } from 'react-icons/fi';
import projectDashboardService from '../../services/projectDashboardService';
import MultiSelectDropdown from '../Common/MultiSelectDropdown';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import './Dashboard.css';
import './ProjectDashboard.css';

const projectCodeOf = (p) => (p.projectCode || p.ProjectCode || '').toString().trim();

const mergeStageCards = (cardLists) => {
  const byKey = new Map();
  cardLists.forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((card) => {
      const key = card.stageCode != null ? String(card.stageCode) : String(card.displayName || card.orderNo || '');
      if (!key) return;
      const existing = byKey.get(key);
      const count = typeof card.count === 'number' ? card.count : 0;
      if (!existing) {
        byKey.set(key, { ...card, count });
        return;
      }
      existing.count += count;
      if (card.orderNo != null && (existing.orderNo == null || card.orderNo < existing.orderNo)) {
        existing.orderNo = card.orderNo;
        if (card.displayName) existing.displayName = card.displayName;
      }
    });
  });
  return Array.from(byKey.values()).sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0));
};

/**
 * Independent Project Dashboard — local project dropdown only.
 * Cards are count-only (no navigation). Does not use ProjectContext / SetProject.
 */
const ProjectDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [stageCards, setStageCards] = useState([]);
  const [pendingActionCount, setPendingActionCount] = useState(0);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const projectOptions = useMemo(
    () =>
      projects
        .map((p) => {
          const code = projectCodeOf(p);
          const desc = (p.projectDescription || p.ProjectDescription || '').toString().trim();
          return { value: code, label: desc ? `${code} — ${desc}` : code };
        })
        .filter((o) => o.value),
    [projects]
  );

  const selectedKey = selectedProjects.join('\u0001');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingProjects(true);
        setError(null);
        const data = await projectDashboardService.getProjects();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setProjects(list);
        if (list.length > 0) {
          const first = projectCodeOf(list[0]);
          if (first) setSelectedProjects([first]);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error || err.message || 'Failed to load projects'
          );
        }
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchDashboardData = useCallback(async (projectCodes) => {
    const codes = (projectCodes || []).map((c) => String(c).trim()).filter(Boolean);
    if (codes.length === 0) {
      setStageCards([]);
      setPendingActionCount(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const results = await Promise.all(
        codes.map(async (projectCode) => {
          const [stageCardsData, kpisData] = await Promise.all([
            projectDashboardService.getStageCards(projectCode),
            projectDashboardService.getKPIs(projectCode),
          ]);
          const kpis = kpisData || {};
          const count = kpis.totalPendingActionItems ?? kpis.TotalPendingActionItems;
          return {
            cards: Array.isArray(stageCardsData) ? stageCardsData : [],
            pending: typeof count === 'number' ? count : 0,
          };
        })
      );

      setStageCards(mergeStageCards(results.map((r) => r.cards)));
      setPendingActionCount(results.reduce((sum, r) => sum + r.pending, 0));
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load dashboard data'
      );
      console.error('ProjectDashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedProjects.length === 0) {
      setStageCards([]);
      setPendingActionCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!cancelled) await fetchDashboardData(selectedProjects);
    })();
    const interval = setInterval(() => {
      if (!cancelled) fetchDashboardData(selectedProjects);
    }, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedKey, fetchDashboardData]);

  if (loadingProjects) return <Spinner />;

  const hasSelection = selectedProjects.length > 0;

  return (
    <div className="dashboard project-dashboard">
      {error && <Alert type="error" message={error} />}

      <div className="project-dashboard-toolbar">
        <label className="project-dashboard-project-label" id="project-dashboard-project-label">
          <FiFolder size={18} aria-hidden />
          <span>Project</span>
        </label>
        <div
          id="project-dashboard-project"
          className="project-dashboard-project-multiselect"
          aria-labelledby="project-dashboard-project-label"
        >
          <MultiSelectDropdown
            placeholder={projects.length === 0 ? 'No projects available' : 'Select project'}
            options={projectOptions}
            selectedValues={selectedProjects}
            onChange={setSelectedProjects}
            disabled={projects.length === 0}
            inlineSummary
            searchable={projectOptions.length > 8}
            searchPlaceholder="Search project…"
          />
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="dashboard-cards">
            {stageCards.map((card, index) => (
              <DashboardCard
                key={card.stageCode ?? card.orderNo}
                icon={getIconForCard(card, index)}
                title={card.displayName ?? ''}
                value={typeof card.count === 'number' ? card.count : 0}
                variant="default"
              />
            ))}
            {hasSelection && (
              <DashboardCard
                icon="⚠️"
                title="Open Issues"
                value={pendingActionCount}
                variant="error"
              />
            )}
          </div>
          {!loading && hasSelection && stageCards.length === 0 && (
            <p className="dashboard-empty-message">
              No stage data for this project. Ensure StageMst has stages for the selected study.
            </p>
          )}
          {!hasSelection && (
            <p className="dashboard-empty-message">
              Select a project to see visit stage cards.
            </p>
          )}
        </>
      )}
    </div>
  );
};

const STAGE_ICONS = {
  101: '📅',
  201: '🔄',
  301: '👁️',
  401: '🔍',
  9999: '✅',
};
const STAGE_ICONS_BY_NAME = {
  schedule: '📅',
  'in progress': '🔄',
  'first review': '👁️',
  'second review': '🔍',
  completed: '✅',
};
const FALLBACK_ICONS = ['📋', '📌', '📍', '📊', '📈', '🎯'];

const getIconForCard = (card, index) => {
  const code = card.stageCode;
  if (code != null && STAGE_ICONS[code]) return STAGE_ICONS[code];
  const name = (card.displayName || '').toLowerCase();
  if (STAGE_ICONS_BY_NAME[name]) return STAGE_ICONS_BY_NAME[name];
  return FALLBACK_ICONS[index % FALLBACK_ICONS.length];
};

/** Display-only card — no click / navigation on Project Dashboard. */
const DashboardCard = ({ icon, title, value, variant = 'default' }) => (
  <div className={`dashboard-card ${variant}`.trim()}>
    <div className="card-icon">{icon}</div>
    <div className="card-title">{title}</div>
    <div className="card-value">{value}</div>
  </div>
);

export default ProjectDashboard;
